import { onDocumentReady, prefersReducedMotion } from "@theme/utilities";
import { ensureGsap } from "@theme/vp-motion";
import { getScrollContainer, scrollContainerMediaQuery } from "@theme/scroll-container";
import Lenis from "https://cdn.jsdelivr.net/npm/lenis@1.3.25/dist/lenis.mjs";
//#region assets/lenis-smooth-scroll.ts
/**
* Lenis needs the same scroll wrapper your theme uses.
* - Desktop (>= 990px): `.page-wrapper` is the scroll container
* - Mobile: `document.scrollingElement` / `document.documentElement` is the scroll container
*
* Important: on desktop, `.page-wrapper` is a flex child with `overflow-y: auto`.
* ResizeObserver may not fire when page content grows (images, fonts, late sections).
* Without `naiveDimensions`, Lenis caches a stale scroll limit and you cannot reach
* the bottom of the page.
*
* When GSAP is available, Lenis is driven from the GSAP ticker so scroll lerp and
* entry tweens share one RAF clock (avoids jaggy dual-loop framing on load).
*
* Mobile (< 990px) skips Lenis entirely — native document scroll only. On iOS,
* Lenis + dynamic toolbar / keyboard viewport changes caused jumpy homepage scroll
* even with `syncTouch: false` (RAF + scroll listeners fighting the browser).
*/
var lenisInstance;
var initialized = false;
/** @type {Element | null} */
var scrollBehaviorTarget = null;
/** @type {string} */
var previousScrollBehavior = "";
/** @type {(() => void) | null} */
var detachLifecycleHandlers = null;
/** @type {((time: number) => void) | null} */
var gsapTickerFn = null;
/** @type {number | null} */
var fallbackRafId = null;
var SCROLL_LOCK_ATTR = "scroll-lock";
var scrollLockObserver;
/**
* Lenis only on desktop squeeze layout (≥990px).
* Phones, tablets, and narrow viewports stay on native document scroll.
* @returns {boolean}
*/
function shouldEnableLenis() {
	return scrollContainerMediaQuery.matches;
}
function isScrollLocked() {
	return document.documentElement.hasAttribute(SCROLL_LOCK_ATTR);
}
function applyScrollLockState() {
	if (!lenisInstance) return;
	if (isScrollLocked()) lenisInstance.stop();
	else lenisInstance.start();
}
/**
* Native `scroll-behavior: smooth` fights Lenis lerp and can leave you short of the true bottom.
* @param {Element | Window} wrapper
*/
function disableNativeSmoothScroll(wrapper) {
	restoreNativeSmoothScroll();
	const target = wrapper instanceof Window ? document.documentElement : wrapper;
	if (!(target instanceof Element)) return;
	scrollBehaviorTarget = target;
	previousScrollBehavior = target.style.scrollBehavior;
	target.style.scrollBehavior = "auto";
}
function restoreNativeSmoothScroll() {
	if (!scrollBehaviorTarget) return;
	scrollBehaviorTarget.style.scrollBehavior = previousScrollBehavior;
	scrollBehaviorTarget = null;
	previousScrollBehavior = "";
}
function stopLenisRaf() {
	if (fallbackRafId != null) {
		cancelAnimationFrame(fallbackRafId);
		fallbackRafId = null;
	}
	if (gsapTickerFn && window.gsap) {
		window.gsap.ticker.remove(gsapTickerFn);
		gsapTickerFn = null;
	}
}
/**
* Drive Lenis from GSAP when possible so motion shares one frame clock.
* @param {typeof window.gsap | null} gsap
*/
function startLenisRaf(gsap) {
	stopLenisRaf();
	if (!lenisInstance) return;
	if (gsap) {
		gsapTickerFn = (time) => {
			lenisInstance?.raf(time * 1e3);
		};
		gsap.ticker.add(gsapTickerFn);
		return;
	}
	const loop = (time) => {
		lenisInstance?.raf(time);
		fallbackRafId = requestAnimationFrame(loop);
	};
	fallbackRafId = requestAnimationFrame(loop);
}
function destroyLenis() {
	stopLenisRaf();
	lenisInstance?.destroy?.();
	lenisInstance = void 0;
	restoreNativeSmoothScroll();
	if ("__lenis" in window) delete window.__lenis;
}
function createLenis() {
	if (prefersReducedMotion()) return;
	if (!shouldEnableLenis()) return;
	const wrapper = getScrollContainer();
	if (!(wrapper instanceof Element) && wrapper !== window) return;
	disableNativeSmoothScroll(wrapper instanceof Element ? wrapper : window);
	lenisInstance = new Lenis({
		wrapper,
		content: wrapper,
		autoRaf: false,
		smoothWheel: true,
		syncTouch: false,
		lerp: .08,
		duration: 1,
		wheelMultiplier: 1,
		anchors: false,
		naiveDimensions: true
	});
	window.__lenis = lenisInstance;
	applyScrollLockState();
	startLenisRaf(window.gsap || null);
}
/**
* Re-sync Lenis with externally restored scroll position.
* Scroll restoration and view transitions can update scrollTop before Lenis
* processes a frame, which causes a visible jump loop until user scroll input.
*/
function syncLenisPosition() {
	if (!lenisInstance) return;
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			if (!lenisInstance) return;
			const currentTop = getScrollContainer()?.scrollTop || 0;
			lenisInstance.resize?.();
			lenisInstance.scrollTo?.(currentTop, {
				immediate: true,
				force: true
			});
			applyScrollLockState();
		});
	});
}
function attachLifecycleHandlers() {
	if (detachLifecycleHandlers) return;
	const onPageHide = () => {
		if (!lenisInstance) return;
		lenisInstance.stop();
	};
	const onPageShow = () => {
		if (!lenisInstance) return;
		syncLenisPosition();
	};
	const onPageReveal = () => {
		if (!lenisInstance) return;
		syncLenisPosition();
	};
	window.addEventListener("pagehide", onPageHide);
	window.addEventListener("pageshow", onPageShow);
	window.addEventListener("pagereveal", onPageReveal);
	detachLifecycleHandlers = () => {
		window.removeEventListener("pagehide", onPageHide);
		window.removeEventListener("pageshow", onPageShow);
		window.removeEventListener("pagereveal", onPageReveal);
		detachLifecycleHandlers = null;
	};
}
function ensureScrollLockObserver() {
	if (scrollLockObserver) return;
	scrollLockObserver = new MutationObserver(() => {
		applyScrollLockState();
	});
	scrollLockObserver.observe(document.documentElement, {
		attributes: true,
		attributeFilter: [SCROLL_LOCK_ATTR]
	});
}
/**
* Tear down / rebuild Lenis when the scroll wrapper breakpoint changes.
* Crossing below 990px destroys the instance so mobile stays fully native.
*/
function recreateLenis() {
	destroyLenis();
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			createLenis();
			applyScrollLockState();
			syncLenisPosition();
		});
	});
}
async function init() {
	if (initialized) return;
	initialized = true;
	if (prefersReducedMotion()) return;
	createLenis();
	ensureScrollLockObserver();
	applyScrollLockState();
	attachLifecycleHandlers();
	syncLenisPosition();
	const gsap = await ensureGsap();
	if (gsap && lenisInstance && !gsapTickerFn) startLenisRaf(gsap);
	window.addEventListener("load", () => lenisInstance?.resize?.(), { once: true });
	scrollContainerMediaQuery.addEventListener("change", recreateLenis);
}
onDocumentReady(init);
//#endregion
